import { KeyboardBackspace } from "@mui/icons-material";
import { IconButton, Typography } from "@mui/material";
import { Box } from "@mui/material";
import {
  Link,
  LoaderFunctionArgs,
  Outlet,
  useLoaderData,
  useOutletContext,
} from "react-router-dom";
import { Tab } from "components/Tab";
import { ParametresSociete, getSocieteInformations } from "api/societe";
import { Indicator } from "components/Indicator";
import { useTranslation } from "react-i18next";

export type ParametrageSocieteContextType = {
  isCreate: boolean;
  parametres: ParametresSociete | null;
  onPortefeuilleUpdated?: () => void;
};

export async function loader({ params }: LoaderFunctionArgs) {
  const societeId = params.id as string;
  const isCreate = societeId === "create";
  const parametres = isCreate ? null : await getSocieteInformations(Number(societeId));
  return { isCreate, parametres };
}

export function Component() {
  const { isCreate, parametres } = useLoaderData() as any;
  const informations = parametres?.informations;

  const { t } = useTranslation("common");

  return (
    <Box width="100%">
      <Box
        width="100%"
        mt={2}
        sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}
      >
        <Box>
          <Link to="/admin-organisation">
            <IconButton
              sx={{
                bgcolor: "primary.main",
                color: "white",
                ":hover": {
                  bgcolor: "primary.main",
                },
              }}
            >
              <KeyboardBackspace />
            </IconButton>
          </Link>
        </Box>

        {!isCreate && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography fontWeight="bold" fontSize={22} color="primary" textAlign="center">
              {informations?.nomSociete} ({informations?.code}){" :"}
            </Typography>
            <Typography color="primary" textAlign="center">
              {informations?.type === "A"
                ? "Assureur"
                : informations?.type === "I"
                  ? "Intermédiaire"
                  : ""}
            </Typography>
          </Box>
        )}

        <Box px={2} />
      </Box>

      {!isCreate && (
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }} mb={2}>
          <Typography color="primary" textAlign="center" width="100%">
            {informations?.email}
          </Typography>
        </Box>
      )}
      {!isCreate && (
        <Box
          sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "center" }}
          mb={2}
        >
          <Indicator color={informations?.actif ? "success.main" : "error.main"} />
          <Typography ml={1} color="primary" textAlign="center">
            {informations?.actif ? "Actif" : "Inactif"}
          </Typography>
        </Box>
      )}
      {!isCreate && (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-around",
            }}
          >
            <Tab to="informations">{t("parametrageSociete.tabs.informations")}</Tab>
            <Tab to="canaux" disabled={isCreate}>
              {t("parametrageSociete.tabs.canaux")}
            </Tab>
            <Tab to="frequences" disabled={isCreate}>
              {t("parametrageSociete.tabs.frequences")}
            </Tab>
            <Tab to="alertes" disabled={isCreate}>
              {t("parametrageSociete.tabs.alertes")}
            </Tab>
            {informations?.type === "A" && (
              <Tab to="portefeuilles" disabled={isCreate}>
                {t("parametrageSociete.tabs.portefeuilles")}
              </Tab>
            )}
          </Box>
        </Box>
      )}

      <Box py={3} px={6}>
        <Outlet context={{ isCreate, parametres } satisfies ParametrageSocieteContextType} />
      </Box>
    </Box>
  );
}

export function useParametrageSociete() {
  return useOutletContext<ParametrageSocieteContextType>();
}
